import api from "../../../api/api";
import { responseError } from "../../../api/apiError";
import { id } from "../../utils";
import {
  allResolved,
  allUnselectedParamsAreOptional,
  allUsesOptional,
  dependency,
  fillCall,
  getFieldValue,
  type Item,
  type ItemsState,
  type ParameterDefinition,
  type StringState,
} from "../ReportServices";

type BooleanState = Record<string, boolean>;

type ReportParamsLoaderState = {
  parameterDefinitions: ParameterDefinition[];
  dataDefinitions: ParameterDefinition[];
  selectedItem: StringState;
  items: ItemsState;
  selectedDataDefinitionIndex: number;
};

type ReportParamsLoaderSetters = {
  setItems: (key: string, items: Item[]) => void;
  setAreItemsLoading: (key: string, value: boolean) => void;
  setCanGetDatas: (value: boolean[]) => void;
  setCanGetData: (value: boolean) => void;
  setError: (value: string) => void;
};

type ReportParamsLoaderOptions = ReportParamsLoaderState & ReportParamsLoaderSetters & {
  authToken: string;
  identityProvider: string;
  newSelectedDataDefinitionIndex?: number | null;
  initial?: boolean;
  changedParameter?: string;
};

export const updateReportParamsCanGetDataState = ({
  parameterDefinitions,
  dataDefinitions,
  selectedItem,
  selectedDataDefinitionIndex,
  setCanGetDatas,
  setCanGetData,
  newSelectedDataDefinitionIndex = null,
}: Pick<ReportParamsLoaderState, "parameterDefinitions" | "dataDefinitions" | "selectedItem" | "selectedDataDefinitionIndex"> &
  Pick<ReportParamsLoaderSetters, "setCanGetDatas" | "setCanGetData"> &
  { newSelectedDataDefinitionIndex?: number | null }) => {
  const allCanGetDatas = dataDefinitions.map(
    (definition) =>
      allResolved(definition.call, selectedItem, parameterDefinitions) ||
      allUnselectedParamsAreOptional(definition.call, selectedItem)
  );
  setCanGetDatas(allCanGetDatas);
  setCanGetData(allCanGetDatas[newSelectedDataDefinitionIndex ?? selectedDataDefinitionIndex]);
};

const buildDropdownRequestBody = (call: string): Record<string, unknown> => {
  let callParts = call.split("&");
  callParts = callParts.map((part) => part.replace(/%26/g, "&"));

  const callEndpoint = callParts[0];
  callParts = callParts.slice(1);

  const body: Record<string, unknown> = {
    apiEndpoint: callEndpoint,
  };

  for (const part of callParts) {
    const eqIndex = part.indexOf("=");
    if (eqIndex > 0) {
      const key = part.substring(0, eqIndex);
      const value = part.substring(eqIndex + 1);
      body[key] = value;
    } else {
      body[part] = true;
    }
  }

  return body;
};

const isItemsParameter = (parameter: ParameterDefinition): boolean =>
  parameter.type.startsWith("select") || parameter.type.startsWith("multiselect");

// A parameter is reloaded when nothing in particular changed, or when it depends on what did.
const isAffectedByChange = (parameter: ParameterDefinition, changedParameter?: string): boolean =>
  !changedParameter || (id(parameter.name) !== changedParameter && dependency(parameter.call, changedParameter));

const getMultiselectSelection = (parameter: ParameterDefinition, rowItems: Item[], selectedValue?: string): string => {
  const values = selectedValue?.split(", ").map((value) => value.trim()).filter(Boolean) ?? [];
  const selectedIds = values.filter((value) => rowItems.some((rowItem) => rowItem.id === value));

  if (selectedIds.length > 0) {
    return selectedIds.join(", ");
  }

  return (parameter.default?.toString().trim().length ?? 0) > 0
    ? rowItems.map((item) => item.id).join(", ")
    : "";
};

export const loadReportParamsDependentData = async (options: ReportParamsLoaderOptions) => {
  const { parameterDefinitions, setAreItemsLoading, changedParameter } = options;

  // The parameters are loaded one after another, so mark every one this pass will touch as loading
  // up front. Otherwise a dropdown waiting its turn keeps offering the items it loaded for the
  // previous selection until its own call starts.
  const loadingNameIds = parameterDefinitions
    .filter((parameter) => isItemsParameter(parameter) && isAffectedByChange(parameter, changedParameter))
    .map((parameter) => id(parameter.name));
  loadingNameIds.forEach((nameId) => setAreItemsLoading(nameId, true));

  try {
    await loadParameterItems(options);
  } finally {
    // Nothing may stay stuck on "Loading..." if one of the loads threw.
    loadingNameIds.forEach((nameId) => setAreItemsLoading(nameId, false));
  }
};

const loadParameterItems = async ({
  parameterDefinitions,
  dataDefinitions,
  selectedItem,
  items,
  selectedDataDefinitionIndex,
  setItems,
  setAreItemsLoading,
  setCanGetDatas,
  setCanGetData,
  setError,
  authToken,
  identityProvider,
  newSelectedDataDefinitionIndex = null,
  initial = false,
  changedParameter,
}: ReportParamsLoaderOptions) => {
  await Excel.run(async (context) => {
    const allParameterDefinitions = parameterDefinitions.concat(dataDefinitions);
    const loadedItems: ItemsState = { ...items };

    for (const parameter of parameterDefinitions) {
      const nameId = id(parameter.name);
      if (!isAffectedByChange(parameter, changedParameter)) continue;

      let call = parameter.call;
      const canLoadNow = allResolved(call, selectedItem, parameterDefinitions) || allUnselectedParamsAreOptional(call, selectedItem);
      if (!canLoadNow) {
        setAreItemsLoading(nameId, false);
        continue;
      }

      const type = parameter.type;
      if (call && (type.startsWith("select") || type.startsWith("multiselect"))) {
        const idField = type.substring(type.indexOf("(") + 1, type.indexOf(",")).trim();
        const nameField = type.substring(type.indexOf(",") + 1, type.indexOf(")")).trim();
        const sorted = !type.endsWith("*") && !type.endsWith("*#") && !type.match(/\*\@\d+$/);
        try {
          setAreItemsLoading(nameId, true);
          call = fillCall(call, selectedItem, null, loadedItems);

          const response = await api.post("dropdown/data", buildDropdownRequestBody(call), authToken, identityProvider);
          if (!response.ok) throw await responseError(response);

          const data: any = await response.json();
          let rowItems: Item[] = data.rows
            .map((row: any) => ({ id: getFieldValue(row, idField), name: getFieldValue(row, nameField) }))
            .filter((item: Item) => item.id != null && item.name != null);
          rowItems = Array.from(new Map(rowItems.map((obj: Item) => [obj.id, obj])).values());

          if (sorted) {
            rowItems.sort((a: Item, b: Item) => a.name.localeCompare(b.name));
          }
          if (!type.startsWith("multiselect") && allUsesOptional(parameter.name, allParameterDefinitions)) {
            rowItems.splice(0, 0, { id: "", name: parameter.default ?? "Unspecified" });
          }

          setItems(nameId, rowItems);
          loadedItems[nameId] = rowItems;
          if (parameter.type.startsWith("multiselect")) {
            if (initial || (parameter.default?.toString().trim().length ?? 0) > 0) {
              selectedItem[nameId] = getMultiselectSelection(parameter, rowItems, initial ? parameter.value : selectedItem[nameId]);
            }
          } else if (!rowItems.find((item) => item.id === selectedItem[nameId])) {
            const defaultValue = parameter.value?.toString()?.trim() || parameter.default?.toString()?.trim() || "";
            selectedItem[nameId] = rowItems.find((item) => item.id === defaultValue) ? defaultValue : "";
          }
        } catch (error) {
          const errMsg = error instanceof Error ? error.message : String(error);
          if (errMsg.includes("payload size")) {
            setError("Cannot load the items for " + nameId + ". Please select fewer dependency items.");
          } else {
            setError("Error fetching " + nameId + " items: " + errMsg);
          }
        } finally {
          setAreItemsLoading(nameId, false);
        }
      } else if (type.startsWith("select") || type.startsWith("multiselect")) {
        try {
          const sheetName = "(" + type.substring(type.indexOf("(") + 1, type.indexOf(")")).trim() + ")";
          const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
          await context.sync();
          const range = sheet.getUsedRange();
          range.load("values");
          await context.sync();

          const rowItems = range.values.map((row: any) => ({ id: row[0], name: row.length > 1 ? row[1] : row[0] }));
          setItems(nameId, rowItems);
          loadedItems[nameId] = rowItems;
          if (type.startsWith("multiselect")) {
            if (initial || (parameter.default?.toString().trim().length ?? 0) > 0) {
              selectedItem[nameId] = getMultiselectSelection(parameter, rowItems, initial ? parameter.value : selectedItem[nameId]);
            }
          } else {
            const defaultValue = parameter.value?.toString()?.trim() || parameter.default?.toString()?.trim() || "";
            selectedItem[nameId] = rowItems?.find((item) => item.id === defaultValue) ? defaultValue : "";
          }
        } finally {
          setAreItemsLoading(nameId, false);
        }
      } else if (type === "text" || type === "number") {
        if (initial) {
          selectedItem[nameId] = parameter.value ?? (parameter.default || "");
        }
      } else if (type === "date" && initial) {
        let defaultValue = parameter.default;
        if (defaultValue === "today") {
          defaultValue = new Date().toISOString().split("T")[0];
        }
        selectedItem[nameId] = parameter.value
          ? new Date(parameter.value).toISOString()
          : defaultValue
            ? new Date(defaultValue + "T12:00:00Z").toISOString()
            : (null as unknown as string);
      }
    }

    updateReportParamsCanGetDataState({
      parameterDefinitions,
      dataDefinitions,
      selectedItem,
      selectedDataDefinitionIndex,
      setCanGetDatas,
      setCanGetData,
      newSelectedDataDefinitionIndex,
    });
  });
};
